/* eslint-disable @typescript-eslint/no-explicit-any */
// Copyright 2016 Sorama B.V.

import { IPolyfill, Polyfills } from "overrides/polyfills/IPolyfill";

export class FullscreenApiDefinition {
    enabled: string;
    element: string;
    request: string;
    exit: string;
    events: {
        change: string;
        error: string;
    };
}

/*
 * Polyfill for the Fullscreen API. Adapted from: https://github.com/neovov/Fullscreen-API-Polyfill
 */
export class FullscreenApiPolyfill implements IPolyfill {
    //#region constants

    // http://dvcs.w3.org/hg/fullscreen/raw-file/tip/Overview.html
    public static apis = new Polyfills<FullscreenApiDefinition>(
        <FullscreenApiDefinition>{
            enabled: "fullscreenEnabled",
            element: "fullscreenElement",
            request: "requestFullscreen",
            exit: "exitFullscreen",
            events: {
                change: "fullscreenchange",
                error: "fullscreenerror",
            },
        },
        <FullscreenApiDefinition>{
            enabled: "webkitFullscreenEnabled",
            element: "webkitCurrentFullScreenElement",
            request: "webkitRequestFullscreen",
            exit: "webkitExitFullscreen",
            events: {
                change: "webkitfullscreenchange",
                error: "webkitfullscreenerror",
            },
        },
        <FullscreenApiDefinition>{
            enabled: "mozFullScreenEnabled",
            element: "mozFullScreenElement",
            request: "mozRequestFullScreen",
            exit: "mozCancelFullScreen",
            events: {
                change: "mozfullscreenchange",
                error: "mozfullscreenerror",
            },
        },
        <FullscreenApiDefinition>{
            enabled: "msFullscreenEnabled",
            element: "msFullscreenElement",
            request: "msRequestFullscreen",
            exit: "msExitFullscreen",
            events: {
                change: "MSFullscreenChange",
                error: "MSFullscreenError",
            },
        }
    );

    public static w3 = FullscreenApiPolyfill.apis.w3;
    //#endregion

    //#region Fields
    private _api: any;
    private _vendor: any;

    public get api(): any {
        return this._api;
    }

    public get vendor(): any {
        return this._vendor;
    }

    private documentAny: Document;
    //#endregion

    constructor() {
        this.documentAny = document;

        // Loop through each vendor's specific API
        for (const api of FullscreenApiPolyfill.apis) {
            // Check if document has the "enabled" property
            if (api.enabled in document) {
                // It seems this browser supports the fullscreen API
                this._api = api;
                break;
            }
        }
    }

    //#region IPolyfill Members
    public use(): IPolyfill {
        if (!(FullscreenApiPolyfill.w3.enabled in document) && this.api) {
            // Add listeners for fullscreen events
            document.addEventListener(this.api.events.change, (ev) => this.handleChange(ev), false);
            document.addEventListener(this.api.events.error, (ev) => this.handleError(ev), false);

            // Copy the default value
            this.documentAny[FullscreenApiPolyfill.w3.enabled] = this.documentAny[this.api.enabled];
            this.documentAny[FullscreenApiPolyfill.w3.element] = this.documentAny[this.api.element];

            const _localThis = this; // Javascript hax, something with closures when overriding a prototype function;

            // Match the reference for exitFullscreen. Use function to maintain 'this' closure for document.
            this.documentAny[FullscreenApiPolyfill.w3.exit] = function () {
                const result = _localThis.documentAny[_localThis.api.exit]();
                return !result ? _localThis.createResolver(FullscreenApiPolyfill.w3.exit) : result;
            };

            // Add the request method to the Element's prototype. Use function to maintain 'this' closure for Element.
            (<unknown>Element.prototype)[FullscreenApiPolyfill.w3.request] = function () {
                // Ignore eslint errors as this is extracted from a repository
                // eslint-disable-next-line prefer-spread, prefer-rest-params
                const result = this[_localThis.api.request].apply(this, arguments);
                return !result ? _localThis.createResolver(FullscreenApiPolyfill.w3.request) : result;
            };
        }

        return this;
    }
    //#endregion

    //#region Helper Methods
    private dispatch(type: string, target: EventTarget): void {
        const event = document.createEvent("Event");

        event.initEvent(type, true, false);
        target.dispatchEvent(event);
    }

    private handleChange(event: Event): void {
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Recopy the enabled and element values
        this.documentAny[FullscreenApiPolyfill.w3.enabled] = this.documentAny[this.api.enabled];
        this.documentAny[FullscreenApiPolyfill.w3.element] = this.documentAny[this.api.element];

        this.dispatch(FullscreenApiPolyfill.w3.events.change, event.target);
    }

    private handleError(event: UIEvent): void {
        this.dispatch(FullscreenApiPolyfill.w3.events.error, event.target);
    }

    private createResolver(methodName: string): Promise<void> {
        return new Promise((resolve: (value?: void | PromiseLike<void>) => void, reject: (reason?: any) => void) => {
            // Reject the promise if asked to exitFullscreen and there is no element currently in fullscreen
            if (methodName === FullscreenApiPolyfill.w3.exit && !this.documentAny[this.api.element]) {
                setTimeout(() => {
                    reject(new TypeError());
                }, 1);
                return;
            }

            let change: (event: UIEvent) => void;
            change = (event: UIEvent) => {
                resolve();
                document.removeEventListener(this.api.events.change, change, false);
            };

            let error: (event: UIEvent) => void;
            error = (event: UIEvent) => {
                reject();
                document.removeEventListener(this.api.events.error, error, false);
            };

            document.addEventListener(this.api.events.change, change, false);
            document.addEventListener(this.api.events.error, error, false);
        });
    }
    //#endregion
}
